import { createAsyncThunk } from '@reduxjs/toolkit';
import { SealOfBiliteracyFormViewModel } from 'src/types/SealOfBiliteracyFormViewModel';
import { SelectOption } from 'src/types/SelectOption';
import {
  isValidCode,
  thunkExecuteAndReturnResultOrShowError,
} from '../../services/base.service';
import {
  deleteSealOfBiliteracyById,
  deleteSealOfBiliteracyFile,
  downloadSealOfBiliteracyFile,
  getAllSealOfBiliteracyFiles,
  getSealOfBiliteracyById,
  getSealOfBiliteracyContact,
  getSealOfBiliteracyFilesByStudent,
  getSealOfBiliteracyFormViewModel,
  getSealOfBiliteracyListItems,
  getSealOfBiliteracySchoolYears,
  postSealOfBiliteracy,
  postSealOfBiliteracyContact,
  putSealOfBiliteracy,
  putSealOfBiliteracyContact,
  uploadSealOfBiliteracyFile,
} from '../../services/sealOfBiliteracy.service';
import { SealOfBiliteracyContactModel } from '../../types/SealOfBiliteracyContactModel';
import { SealOfBiliteracyFileListItemModel } from '../../types/SealOfBiliteracyFileListItemModel';
import { SealOfBiliteracyListItemModel } from '../../types/SealOfBiliteracyListItemModel';
import { SealOfBiliteracyModel } from '../../types/SealOfBiliteracyModel';

export const fetchSealOfBiliteracyListItems = createAsyncThunk(
  'sealOfBiliteracy/fetchSealOfBiliteracyListItems',
  async (calendarYear: string, thunkAPI) => {
    return await thunkExecuteAndReturnResultOrShowError<
      SealOfBiliteracyListItemModel[]
    >(
      thunkAPI,
      getSealOfBiliteracyListItems(calendarYear, {
        signal: thunkAPI.signal as AbortSignal,
      })
    );
  }
);

export const fetchSealOfBiliteracyById = createAsyncThunk(
  'sealOfBiliteracy/fetchSealOfBiliteracyById',
  async (id: string, thunkAPI) => {
    return await thunkExecuteAndReturnResultOrShowError<SealOfBiliteracyModel>(
      thunkAPI,
      getSealOfBiliteracyById(id, { signal: thunkAPI.signal as AbortSignal })
    );
  }
);

export const softDeleteSealOfBiliteracyById = createAsyncThunk(
  'sealOfBiliteracy/softDeleteSealOfBiliteracyById',
  async (id: string, thunkAPI) => {
    return await thunkExecuteAndReturnResultOrShowError<SealOfBiliteracyModel>(
      thunkAPI,
      deleteSealOfBiliteracyById(id, { signal: thunkAPI.signal as AbortSignal })
    );
  }
);

// Contact thunks
export const fetchSealOfBiliteracyContact = createAsyncThunk(
  'sealOfBiliteracy/fetchSealOfBiliteracyContact',
  async (
    options: { showLoading?: boolean } = { showLoading: true },
    thunkAPI
  ) => {
    try {
      const response = await getSealOfBiliteracyContact({
        signal: thunkAPI.signal as AbortSignal,
      });

      // Handle 404 errors gracefully - return empty contact data instead of showing error
      if (response.status === 404) {
        return {
          data: {
            id: '',
            leaIdentifier: '',
            firstName: '',
            lastOrSurname: '',
            email: '',
            telephone: '',
            documentsUploaded: false,
          } as SealOfBiliteracyContactModel,
          showLoading: options.showLoading,
        };
      }

      // For other errors, use the standard error handling
      if (!isValidCode(response.status)) {
        if (!response.aborted) {
          // Use thunkExecuteAndReturnResultOrShowError for standard error handling
          return await thunkExecuteAndReturnResultOrShowError<{ id: string }>(
            thunkAPI,
            Promise.resolve(response)
          );
        }
        return {
          data: {
            id: '',
            leaIdentifier: '',
            firstName: '',
            lastOrSurname: '',
            email: '',
            telephone: '',
            documentsUploaded: false,
          } as SealOfBiliteracyContactModel,
          showLoading: options.showLoading,
        };
      }

      return { data: response.data, showLoading: options.showLoading };
    } catch (error) {
      // Handle any unexpected errors
      console.warn(
        'Failed to fetch seal of biliteracy contact, using default values:',
        error
      );
      return {
        data: {
          id: '',
          leaIdentifier: '',
          firstName: '',
          lastOrSurname: '',
          email: '',
          telephone: '',
          documentsUploaded: false,
        } as SealOfBiliteracyContactModel,
        showLoading: options.showLoading,
      };
    }
  }
);

export const fetchSealOfBiliteracySchoolYears = createAsyncThunk(
  'input/fetchSealOfBiliteracySchoolYears',
  async (_, thunkAPI) => {
    return await thunkExecuteAndReturnResultOrShowError<SelectOption[]>(
      thunkAPI,
      getSealOfBiliteracySchoolYears({ signal: thunkAPI.signal })
    );
  }
);

export const createSealOfBiliteracyContact = createAsyncThunk(
  'sealOfBiliteracy/createSealOfBiliteracyContact',
  async (
    contact: SealOfBiliteracyContactModel,
    thunkAPI
  ): Promise<SealOfBiliteracyContactModel> => {
    return await thunkExecuteAndReturnResultOrShowError<SealOfBiliteracyContactModel>(
      thunkAPI,
      postSealOfBiliteracyContact(contact, {
        signal: thunkAPI.signal as AbortSignal,
      })
    );
  }
);

export const updateSealOfBiliteracyContact = createAsyncThunk(
  'sealOfBiliteracy/updateSealOfBiliteracyContact',
  async (
    contact: SealOfBiliteracyContactModel,
    thunkAPI
  ): Promise<SealOfBiliteracyContactModel> => {
    return await thunkExecuteAndReturnResultOrShowError<SealOfBiliteracyContactModel>(
      thunkAPI,
      putSealOfBiliteracyContact(contact, {
        signal: thunkAPI.signal as AbortSignal,
      })
    );
  }
);

export const fetchSealOfBiliteracyFormViewModel = createAsyncThunk(
  'sealOfBiliteracy/fetchSealOfBiliteracyFormViewModel',
  async (_, thunkAPI) => {
    return await thunkExecuteAndReturnResultOrShowError<SealOfBiliteracyFormViewModel>(
      thunkAPI,
      getSealOfBiliteracyFormViewModel({
        signal: thunkAPI.signal as AbortSignal,
      })
    );
  }
);

export const createSealOfBiliteracy = createAsyncThunk(
  'sealOfBiliteracy/createSealOfBiliteracy',
  async (
    sealOfBiliteracy: SealOfBiliteracyModel,
    thunkAPI
  ): Promise<SealOfBiliteracyModel> => {
    return await thunkExecuteAndReturnResultOrShowError<SealOfBiliteracyModel>(
      thunkAPI,
      postSealOfBiliteracy(sealOfBiliteracy, {
        signal: thunkAPI.signal as AbortSignal,
      })
    );
  }
);

export const updateSealOfBiliteracy = createAsyncThunk(
  'sealOfBiliteracy/updateSealOfBiliteracy',
  async (
    sealOfBiliteracy: SealOfBiliteracyModel,
    thunkAPI
  ): Promise<SealOfBiliteracyModel> => {
    return await thunkExecuteAndReturnResultOrShowError<SealOfBiliteracyModel>(
      thunkAPI,
      putSealOfBiliteracy(sealOfBiliteracy, {
        signal: thunkAPI.signal as AbortSignal,
      })
    );
  }
);

// File-related thunks
export const uploadSealOfBiliteracyFileThunk = createAsyncThunk(
  'sealOfBiliteracy/uploadFile',
  async (
    params: {
      schoolYear: string; studentIdentifier: string;
      leaIdentifier: string; file: File;
    },
    thunkAPI
  ): Promise<{ id: string }> => {
    const response = await uploadSealOfBiliteracyFile(
      params.schoolYear,
      params.studentIdentifier,
      params.leaIdentifier,
      params.file,
      {
        signal: thunkAPI.signal as AbortSignal,
      }
    );

    if (!isValidCode(response.status)) {
      // Don't dispatch global error for upload failures - handle locally in component
      // Just throw an error to make the thunk rejected
      const errorMessage =
        response.error?.detail ||
        response.error?.title ||
        'Failed to upload file. Please try again.';
      throw new Error(errorMessage);
    }

    return response.data;
  }
);

export const deleteSealOfBiliteracyFileThunk = createAsyncThunk(
  'sealOfBiliteracy/deleteFile',
  async (params: { leaId: string; id: string }, thunkAPI): Promise<void> => {
    return await thunkExecuteAndReturnResultOrShowError<void>(
      thunkAPI,
      deleteSealOfBiliteracyFile(params.leaId, params.id, {
        signal: thunkAPI.signal as AbortSignal,
      })
    );
  }
);

export const fetchAllSealOfBiliteracyFilesThunk = createAsyncThunk(
  'sealOfBiliteracy/fetchAllFiles',
  async (
    leaId: string,
    thunkAPI
  ): Promise<SealOfBiliteracyFileListItemModel[]> => {
    return await thunkExecuteAndReturnResultOrShowError<
      SealOfBiliteracyFileListItemModel[]
    >(
      thunkAPI,
      getAllSealOfBiliteracyFiles(leaId, {
        signal: thunkAPI.signal as AbortSignal,
      })
    );
  }
);

export const downloadSealOfBiliteracyFileThunk = createAsyncThunk(
  'sealOfBiliteracy/downloadFile',
  async (
    params: { leaId: string; metaId: string; fileName: string },
    thunkAPI
  ): Promise<void> => {
    try {
      const blob = await downloadSealOfBiliteracyFile(
        params.leaId,
        params.metaId,
        {
          signal: thunkAPI.signal as AbortSignal,
        }
      );

      // Create download link and trigger download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = params.fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      // For download errors, throw an error to be handled by the thunk
      throw new Error('Failed to download file');
    }
  }
);

export const fetchSealOfBiliteracyFilesByStudentThunk = createAsyncThunk(
  'sealOfBiliteracy/fetchFilesByStudent',
  async (
    params: {
      schoolYear: string;
      leaId: string;
      msisId: string;
      showLoading?: boolean;
    },
    thunkAPI
  ): Promise<SealOfBiliteracyFileListItemModel[]> => {
    return await thunkExecuteAndReturnResultOrShowError<
      SealOfBiliteracyFileListItemModel[]
    >(
      thunkAPI,
      getSealOfBiliteracyFilesByStudent(
        params.schoolYear,
        params.leaId,
        params.msisId,
        {
          signal: thunkAPI.signal as AbortSignal,
        }
      )
    );
  }
);
